import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, CartProvider, useAuth } from './context/AppContext';
import { LanguageProvider } from './context/LanguageContext';
import { ShopProvider, useShop } from './context/ShopContext';
import { Toaster } from './components/ui/sonner';
import CookieConsent from './components/CookieConsent';
import SupportAssistantWidget from './components/support/SupportAssistantWidget';
import { SupportAssistantProvider } from './context/SupportAssistantContext';
import { bootstrapAnalytics, trackPageView } from './lib/analytics';

// All page & shell components are lazy-loaded.
// /app/* pages stay strictly out of the initial marketing bundle.
const Header = lazy(() => import('./components/Header'));
const BottomNav = lazy(() => import('./components/BottomNav'));

const AuthPage = lazy(() => import('./pages/AuthPage'));
const MenuPage = lazy(() => import('./pages/MenuPage'));
const ShopSelectionScreen = lazy(() => import('./pages/ShopSelectionScreen'));
const ShopDetailPage = lazy(() => import('./pages/ShopDetailPage'));
const EstablishmentDetailPage = lazy(() => import('./pages/EstablishmentDetailPage'));

// Lazy loaded pages (code splitting)
const WalletPage = lazy(() => import('./pages/WalletPage'));
const LocationPage = lazy(() => import('./pages/LocationPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const POSPage = lazy(() => import('./pages/POSPage'));
const PreparationPage = lazy(() => import('./pages/PreparationPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));
const P0Maintenance = lazy(() => import('./pages/P0Maintenance'));
const OnlineRechargePage = lazy(() => import('./pages/OnlineRechargePage'));
const HomeV3 = lazy(() => import('./pages/HomeV3'));
const ActusPage = lazy(() => import('./pages/ActusPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const MyReviewsPage = lazy(() => import('./pages/MyReviewsPage'));
// LCP : préchauffage du chunk Landing pour / et /en — le téléchargement démarre
// dès l'évaluation du bundle au lieu d'attendre le premier rendu React.
const landingImport = () => import('./pages/LandingPage');
if (typeof window !== 'undefined' && ['/', '/en', '/en/'].includes(window.location.pathname)) {
  landingImport();
}
const LandingPage = lazy(landingImport);
const AppPreviewPage = lazy(() => import('./pages/AppPreviewPage'));
const BlogPage = lazy(() => import(/* webpackChunkName: "marketing-blog-hub-v2" */ './pages/BlogPage'));
const ArticleMachineCafe = lazy(() => import('./pages/ArticleMachineCafe'));
const ArticleBudgetCafe = lazy(() => import('./pages/ArticleBudgetCafe'));
const ArticleFranchiseMaroc = lazy(() => import('./pages/ArticleFranchiseMaroc'));
const ArticleCoffeeShopTunisie = lazy(() => import('./pages/ArticleCoffeeShopTunisie'));
const ArticleCoffeeShopSenegal = lazy(() => import('./pages/ArticleCoffeeShopSenegal'));
const ArticleEmplacementCoffeeShop = lazy(() => import('./pages/ArticleEmplacementCoffeeShop'));
const ArticleConceptsHybrides = lazy(() => import('./pages/ArticleConceptsHybrides'));
const ArticleBarberCoffeeShop = lazy(() => import('./pages/ArticleBarberCoffeeShop'));
const ArticleCoffeeShopAfriqueVsEurope = lazy(() => import('./pages/ArticleCoffeeShopAfriqueVsEurope'));
const ArticleTorreactionCoffeeShop = lazy(() => import('./pages/ArticleTorreactionCoffeeShop'));
const ArticleCartesFidelitePapier = lazy(() => import('./pages/ArticleCartesFidelitePapier'));
const ArticleWalletClient = lazy(() => import('./pages/ArticleWalletClient'));
const ArticleWalletHistoireAvenir = lazy(() => import('./pages/ArticleWalletHistoireAvenir'));
const ConseillerPrototypePage = lazy(() => import('./pages/ConseillerPrototypePage'));
const FormulesPage = lazy(() => import('./pages/FormulesPage'));
const PaiementVirementPage = lazy(() => import('./pages/PaiementVirementPage'));
const LogicielCaisseRestaurantPage = lazy(() => import('./pages/LogicielCaisseRestaurantPage'));
const CafePosSoftwarePage = lazy(() => import('./pages/CafePosSoftwarePage'));
const RestaurantManagementSoftwarePage = lazy(() => import('./pages/RestaurantManagementSoftwarePage'));
const LogicielRestaurantPage = lazy(() => import('./pages/LogicielRestaurantPage'));
const LogicielCoffeeShopPage = lazy(() => import('./pages/LogicielCoffeeShopPage'));
const ProgrammeFideliteRestaurantPage = lazy(() => import('./pages/ProgrammeFideliteRestaurantPage'));
const CrmRestaurantPage = lazy(() => import('./pages/CrmRestaurantPage'));
const RestaurantDigitalMenuPage = lazy(() => import('./pages/RestaurantDigitalMenuPage'));
const PaiementQrCodeRestaurantPage = lazy(() => import('./pages/PaiementQrCodeRestaurantPage'));
const ArticleGererRestaurantFrance = lazy(() => import('./pages/ArticleGererRestaurantFrance'));
const ArticleChainesRestaurationTech = lazy(() => import('./pages/ArticleChainesRestaurationTech'));
const ArticlePiloterRestaurantDistance = lazy(() => import('./pages/ArticlePiloterRestaurantDistance'));
const ArticleAugmenterChiffreAffaires = lazy(() => import('./pages/ArticleAugmenterChiffreAffaires'));
const ArticleTicketMoyen = lazy(() => import('./pages/ArticleTicketMoyen'));
const ArticleReduireGaspillage = lazy(() => import('./pages/ArticleReduireGaspillage'));
const ArticleApplicationCaisseRestaurant = lazy(() => import('./pages/ArticleApplicationCaisseRestaurant'));
const ArticleLogicielFastFood = lazy(() => import('./pages/ArticleLogicielFastFood'));
const ArticleLogicielBoulangerie = lazy(() => import('./pages/ArticleLogicielBoulangerie'));
const ArticleLogicielCaisseBoulangerie = lazy(() => import('./pages/ArticleLogicielCaisseBoulangerie'));
const ArticleLogicielPizzeria = lazy(() => import('./pages/ArticleLogicielPizzeria'));
const ArticleLogicielCaissePizzeria = lazy(() => import('./pages/ArticleLogicielCaissePizzeria'));
const ArticleSansContactCaisse = lazy(() => import('./pages/ArticleSansContactCaisse'));
const ArticleProgrammeFidelite = lazy(() => import('./pages/ArticleProgrammeFidelite'));
const ArticleGaspillageAlimentaire = lazy(() => import('./pages/ArticleGaspillageAlimentaire'));
const ArticleRestaurationAfrique = lazy(() => import('./pages/ArticleRestaurationAfrique'));
const ArticleComparatifLightspeed = lazy(() => import('./pages/ArticleComparatifLightspeed'));
const ArticleComparatifLaddition = lazy(() => import('./pages/ArticleComparatifLaddition'));
const ArticleComparatifZelty = lazy(() => import('./pages/ArticleComparatifZelty'));
const ArticleComparatifSquare = lazy(() => import('./pages/ArticleComparatifSquare'));
const ArticleRestaurantPosSoftwareGuide = lazy(() => import('./pages/ArticleRestaurantPosSoftwareGuide'));
const ArticleAppRestaurant = lazy(() => import('./pages/ArticleAppRestaurant'));
const ArticleOuvrirRestaurant = lazy(() => import('./pages/ArticleOuvrirRestaurant'));
const ArticleRestaurantApp = lazy(() => import('./pages/ArticleRestaurantApp'));
const ArticleWhyRestaurantNotProfitable = lazy(() => import('./pages/ArticleWhyRestaurantNotProfitable'));
const ArticleFacturationElectronique = lazy(() => import(/* webpackChunkName: "marketing-blog-facturation-electronique" */ './pages/ArticleFacturationElectronique'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const WorkspaceInvitationOnboardPage = lazy(() => import('./pages/WorkspaceInvitationOnboardPage'));
const OSPage = lazy(() => import('./pages/OSPage'));
const PlatformPage = lazy(() => import('./pages/PlatformPage'));
const ClientDossierPage = lazy(() => import('./pages/platform/ClientDossierPage'));
const OSSubscriptionPage = lazy(() => import('./pages/OSSubscriptionPage'));
const OSPaymentsPage = lazy(() => import('./pages/OSPaymentsPage'));
const OSPaymentsReturnPage = lazy(() => import('./pages/OSPaymentsReturnPage'));
const OSPaymentsRefreshPage = lazy(() => import('./pages/OSPaymentsRefreshPage'));
const OSRoadmapPage = lazy(() => import('./pages/OSRoadmapPage'));
const WorkspaceTeamPage = lazy(() => import('./pages/WorkspaceTeamPage'));
const WorkspaceDashboardPage = lazy(() => import('./pages/WorkspaceDashboardPage'));
const WorkspaceSettingsPage = lazy(() => import('./pages/WorkspaceSettingsPage'));
const WorkspaceMyAccountPage = lazy(() => import('./pages/WorkspaceMyAccountPage'));
const EstablishmentsPage = lazy(() => import('./pages/EstablishmentsPage'));
const OnboardingWizard = lazy(() => import('./pages/OnboardingWizard'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const TermsMaterielPage = lazy(() => import('./pages/TermsMaterielPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const DpaPage = lazy(() => import('./pages/DpaPage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'));
const PourquoiSuperkawaPage = lazy(() => import('./pages/PourquoiSuperkawaPage'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="w-10 h-10 border-4 border-[#2F5D46] border-t-transparent rounded-full animate-spin" />
  </div>
);

// MB-SEO-ROBOTS-AUTH-1 — routes techniques d'authentification/onboarding sans
// valeur SEO (formulaires, liens à usage unique) : noindex,nofollow au même
// titre que /app/*. N'affecte aucune route marketing/blog/comparaison.
const NOINDEX_TECHNICAL_ROUTES = ['/login', '/platform/login', '/register', '/verify-email', '/invitation', '/delete-account'];

// Meta robots controller — noindex on /app/* et routes techniques, index sur le marketing
const RobotsMetaController = () => {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname;
    const isAppRoute = path.startsWith('/app');
    const isTechnicalAuthRoute = NOINDEX_TECHNICAL_ROUTES.some(
      (r) => path === r || path.startsWith(`${r}/`)
    );
    let tag = document.querySelector('meta[name="robots"]');
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'robots');
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', (isAppRoute || isTechnicalAuthRoute) ? 'noindex, nofollow' : 'index, follow');
  }, [location.pathname]);
  return null;
};

// OS-2A — Helper: résout la home d'un staff selon son rôle
const resolveStaffHome = (role) => {
  if (role === 'owner' || role === 'manager') return '/app/os';
  if (role === 'barista') return '/app/pos';
  return '/app/wallet'; // fallback rôle absent/ambigu
};

// Analytics page_view controller — émet un page_view à chaque changement de route.
// GA4 reste exclu de /app/* (après consentement). PostHog (legacy) désactivé — voir lib/analytics.js.
const AnalyticsListener = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
};

// Protected Route Wrapper
const ProtectedRoute = ({ children, allowedRoles = ['customer', 'barista', 'manager', 'owner'] }) => {
  const { token, loading, role } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!token) {
    return <Navigate to="/app/auth" replace />;
  }

  if (!allowedRoles.includes(role)) {
    return <Navigate to="/app/wallet" replace />;
  }

  return children;
};

// Staff Route Wrapper
const StaffRoute = ({ children }) => {
  const { token, loading, role } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!['barista', 'manager', 'owner'].includes(role)) {
    return <Navigate to="/app/wallet" replace />;
  }

  return children;
};

// Admin Route Wrapper — accès owner + manager uniquement (barista bloqué → POS)
const AdminRoute = ({ children }) => {
  const { token, loading, role } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!['manager', 'owner'].includes(role)) {
    // MB-TEAM-1 : barista n'accède qu'au POS ; customer → wallet
    if (role === 'barista') return <Navigate to="/app/pos" replace />;
    return <Navigate to="/app/wallet" replace />;
  }

  return children;
};

// OS Route Wrapper — accès owner + manager uniquement (barista et customer bloqués)
const OSRoute = ({ children }) => {
  const { token, loading, role } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!['manager', 'owner'].includes(role)) {
    // Barista → POS ; customer → wallet
    if (role === 'barista') return <Navigate to="/app/pos" replace />;
    return <Navigate to="/app/wallet" replace />;
  }

  return children;
};

// Platform Route Wrapper (MB-NAV-1) — réservé aux admins plateforme SUPERKAWA
// (allowlist env via is_platform_admin sur /users/me). Sinon redirection propre vers la home adaptée.
const PlatformRoute = ({ children }) => {
  const { token, loading, role, isPlatformAdmin } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!token) {
    return <Navigate to="/platform/login" replace />;
  }

  if (!isPlatformAdmin) {
    return <Navigate to={resolveStaffHome(role)} replace />;
  }

  return children;
};

// Main App Layout — with shop gate for customers
const AppLayout = ({ children, showNav = true }) => {
  const { selectedShop, loading: shopLoading } = useShop();

  if (shopLoading) return <PageLoader />;

  if (!selectedShop) return (
    <Suspense fallback={<PageLoader />}>
      <ShopSelectionScreen />
    </Suspense>
  );

  return (
    <div className="min-h-screen bg-background">
      <Suspense fallback={null}><Header /></Suspense>
      <main className="pb-20 max-w-[430px] mx-auto">{children}</main>
      {showNav && <Suspense fallback={null}><BottomNav /></Suspense>}
    </div>
  );
};

// Staff Layout (no bottom nav)
const StaffLayout = ({ children }) => (
  <div className="min-h-screen bg-background">
    {children}
  </div>
);

function AppRoutes() {
  const { token, role } = useAuth();

  return (
    <Suspense fallback={<PageLoader />}>
      <RobotsMetaController />
      <AnalyticsListener />
      <Toaster position="top-center" richColors />
      <CookieConsent />
      {/* MB-AI-SUPPORT-2C — bouton flottant marketing, s'auto-masque hors allowlist (config/supportAssistantRoutes.js) */}
      <SupportAssistantWidget surface="marketing" variant="floating" />
      <Routes>
        {/* ═══════ PUBLIC MARKETING ROUTES ═══════ */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/offre" element={<Navigate to="/solutions" replace />} />
        <Route path="/app-preview" element={<AppPreviewPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/en/blog" element={<BlogPage />} />
        <Route path="/blog/machine-cafe-professionnelle" element={<ArticleMachineCafe />} />
        <Route path="/blog/chaines-restauration-avancees-technologiquement" element={<ArticleChainesRestaurationTech />} />
        <Route path="/blog/piloter-restaurant-a-distance" element={<ArticlePiloterRestaurantDistance />} />
        <Route path="/blog/augmenter-chiffre-affaires-restaurant" element={<ArticleAugmenterChiffreAffaires />} />
        <Route path="/blog/augmenter-ticket-moyen-restaurant" element={<ArticleTicketMoyen />} />
        <Route path="/blog/reduire-gaspillage-alimentaire-restaurant" element={<ArticleReduireGaspillage />} />
        <Route path="/blog/facturation-electronique-restaurateurs-guide-complet" element={<ArticleFacturationElectronique />} />
        <Route path="/blog/budget-cafe-algerie" element={<ArticleBudgetCafe />} />
        <Route path="/blog/franchise-coffee-shop-maroc" element={<ArticleFranchiseMaroc />} />
        <Route path="/blog/coffee-shop-tunisie" element={<ArticleCoffeeShopTunisie />} />
        <Route path="/blog/ouvrir-coffee-shop-senegal-2026" element={<ArticleCoffeeShopSenegal />} />
        <Route path="/blog/emplacement-coffee-shop-rentable" element={<ArticleEmplacementCoffeeShop />} />
        <Route path="/blog/idees-concepts-coffee-shop-hybrides" element={<ArticleConceptsHybrides />} />
        <Route path="/blog/barber-shop-coffee-shop-2026" element={<ArticleBarberCoffeeShop />} />
        <Route path="/blog/coffee-shop-afrique-vs-europe" element={<ArticleCoffeeShopAfriqueVsEurope />} />
        <Route path="/blog/torrefaction-coffee-shop-2026" element={<ArticleTorreactionCoffeeShop />} />
        <Route path="/blog/cartes-fidelite-papier-coffee-shop" element={<ArticleCartesFidelitePapier />} />
        <Route path="/blog/wallet-client-coffee-shop" element={<ArticleWalletClient />} />
        <Route path="/blog/wallet-client-restaurant-histoire-avenir" element={<ArticleWalletHistoireAvenir />} />
        <Route path="/blog/gerer-restaurant-france-defis" element={<ArticleGererRestaurantFrance />} />
        <Route path="/blog/app-restaurant" element={<ArticleAppRestaurant />} />
        <Route path="/blog/ouvrir-un-restaurant" element={<ArticleOuvrirRestaurant />} />
        <Route path="/blog/application-caisse-restaurant-comment-choisir" element={<ArticleApplicationCaisseRestaurant />} />
        <Route path="/blog/logiciel-caisse-fast-food" element={<ArticleLogicielFastFood />} />
        <Route path="/blog/logiciel-boulangerie-guide-achat" element={<ArticleLogicielBoulangerie />} />
        <Route path="/blog/logiciel-caisse-boulangerie-pos" element={<ArticleLogicielCaisseBoulangerie />} />
        <Route path="/blog/logiciel-pizzeria-guide-achat" element={<ArticleLogicielPizzeria />} />
        <Route path="/blog/logiciel-caisse-pizzeria-pos" element={<ArticleLogicielCaissePizzeria />} />
        <Route path="/blog/sans-contact-caisse-restaurant" element={<ArticleSansContactCaisse />} />
        <Route path="/blog/programme-fidelite-frequence-visite" element={<ArticleProgrammeFidelite />} />
        <Route path="/blog/gaspillage-alimentaire-restaurant" element={<ArticleGaspillageAlimentaire />} />
        <Route path="/blog/restauration-afrique-francophone-digitalisation" element={<ArticleRestaurationAfrique />} />
        {/* Routes EN — articles connexes piliers (MB-MKT-I18N-5c) */}
        <Route path="/en/blog/coffee-shop-customer-wallet" element={<ArticleWalletClient />} />
        <Route path="/en/blog/paper-loyalty-cards-coffee-shops" element={<ArticleCartesFidelitePapier />} />
        <Route path="/en/blog/loyalty-program-visit-frequency" element={<ArticleProgrammeFidelite />} />
        <Route path="/en/blog/contactless-restaurant-pos" element={<ArticleSansContactCaisse />} />
        <Route path="/en/blog/restaurant-customer-wallet-history-future" element={<ArticleWalletHistoireAvenir />} />
        <Route path="/en/blog/restaurant-pos-software-guide" element={<ArticleRestaurantPosSoftwareGuide />} />
        <Route path="/en/blog/restaurant-app" element={<ArticleRestaurantApp />} />
        <Route path="/en/blog/why-your-restaurant-isnt-profitable" element={<ArticleWhyRestaurantNotProfitable />} />
        {/* MB-OS-ADVISOR-SOLUTIONS-MIGRATION-V1 — /solutions = Conseiller SUPERKAWA OS (expérience officielle) */}
        <Route path="/solutions" element={<ConseillerPrototypePage />} />
        {/* /conseiller → /solutions : redirection SPA (client-side). Un vrai 301 HTTP est défini côté déploiement (vercel.json redirects). */}
        <Route path="/conseiller" element={<Navigate to="/solutions" replace />} />
        <Route path="/formules" element={<FormulesPage />} />
        {/* MB-BRAND-PAGE-1 — Texte fondateur officiel de la marque (manifeste, FR uniquement, indexable) */}
        <Route path="/pourquoi-superkawa" element={<PourquoiSuperkawaPage />} />
        <Route path="/tarifs" element={<Navigate to="/formules" replace />} />
        <Route path="/paiement-virement" element={<PaiementVirementPage />} />
        {/* /simulateur fusionnée dans le Conseiller — redirection SPA (client-side). Un vrai 301 HTTP est défini côté déploiement (vercel.json redirects). */}
        <Route path="/simulateur" element={<Navigate to="/solutions" replace />} />
        <Route path="/abonnement" element={<Navigate to="/" replace />} />

        {/* ═══════ LEGAL ROUTES (publication verbatim v1.1) ═══════ */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/cgv-materiel" element={<TermsMaterielPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        <Route path="/dpa" element={<DpaPage />} />
        <Route path="/legal" element={<LegalPage />} />
        {/* PLAY-COMPLIANCE-1 — Page publique de suppression de compte (Google Play Data Safety) */}
        <Route path="/delete-account" element={<DeleteAccountPage />} />

        {/* ═══════ NEW LOGIN ENTRY (LOGIN-1A) — coexiste avec /app/auth ═══════ */}
        <Route path="/login" element={<LoginPage />} />

        {/* ═══════ GATEWAY PLATEFORME (P1) — Super-Admin dédié, réutilise LoginPage ═══════ */}
        <Route path="/platform/login" element={<LoginPage mode="platform" />} />

        {/* ═══════ MB-SIGNUP-2b — Inscription owner publique (email-first, semi-fermée) ═══════ */}
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/invitation/:token" element={<WorkspaceInvitationOnboardPage />} />

        {/* MB-SEO-P1-A — Page enfant SEO commerciale (caisse) */}
        <Route path="/logiciel-caisse-restaurant" element={<LogicielCaisseRestaurantPage />} />

        {/* MB-SEO-P1-C — Page pilier parent (hub SaaS restauration) */}
        <Route path="/logiciel-restaurant" element={<LogicielRestaurantPage />} />

        {/* MB-SEO-COFFEESHOP-SOFTWARE-FR-1 — Page pilier « logiciel coffee shop » */}
        <Route path="/logiciel-coffee-shop" element={<LogicielCoffeeShopPage />} />

        {/* MB-SEO — Page pilier programme de fidélité restaurant */}
        <Route path="/programme-fidelite-restaurant" element={<ProgrammeFideliteRestaurantPage />} />
        <Route path="/crm-restaurant" element={<CrmRestaurantPage />} />

        {/* MB-SEO — Page pilier paiement QR code restaurant */}
        <Route path="/paiement-qr-code-restaurant" element={<PaiementQrCodeRestaurantPage />} />

        {/* SEO — Page comparatif logiciel de caisse */}
        <Route path="/comparatif/superkawa-vs-lightspeed" element={<ArticleComparatifLightspeed />} />
        <Route path="/comparatif/superkawa-vs-laddition" element={<ArticleComparatifLaddition />} />
        <Route path="/comparatif/superkawa-vs-zelty" element={<ArticleComparatifZelty />} />
        <Route path="/comparatif/superkawa-vs-square" element={<ArticleComparatifSquare />} />
        <Route path="/en/comparison/superkawa-vs-lightspeed" element={<ArticleComparatifLightspeed />} />
        <Route path="/en/comparison/superkawa-vs-square" element={<ArticleComparatifSquare />} />
        <Route path="/en/comparison/superkawa-vs-zelty" element={<ArticleComparatifZelty />} />
        <Route path="/en/comparison/superkawa-vs-laddition" element={<ArticleComparatifLaddition />} />

        {/* ═══════ EN MARKETING ROUTES (MB-MKT-I18N-1) — architecture only ═══════ */}
        {/* Les routes EN réutilisent temporairement les composants FR (placeholders). */}
        {/* La traduction du contenu arrivera dans MB-MKT-I18N-2 → 5. URLs FR inchangées. */}
        <Route path="/en" element={<LandingPage />} />
        <Route path="/en/solutions" element={<ConseillerPrototypePage />} />
        <Route path="/en/pricing" element={<FormulesPage />} />
        <Route path="/en/features" element={<AppPreviewPage />} />
        <Route path="/en/restaurant-pos-software" element={<LogicielCaisseRestaurantPage />} />
        <Route path="/en/cafe-pos-software" element={<CafePosSoftwarePage />} />
        <Route path="/en/restaurant-management-software" element={<RestaurantManagementSoftwarePage />} />
        <Route path="/en/restaurant-software" element={<LogicielRestaurantPage />} />
        <Route path="/en/restaurant-loyalty-program" element={<ProgrammeFideliteRestaurantPage />} />
        <Route path="/en/restaurant-qr-payments" element={<PaiementQrCodeRestaurantPage />} />
        <Route path="/en/restaurant-digital-menu" element={<RestaurantDigitalMenuPage />} />

        {/* ═══════ APP ENTRY POINT ═══════ */}
        <Route
          path="/app"
          element={
            token ? (
              role && ['barista', 'manager', 'owner'].includes(role) ? (
                <Navigate to={resolveStaffHome(role)} replace />
              ) : (
                <Navigate to="/app/wallet" replace />
              )
            ) : (
              <Navigate to="/app/auth" replace />
            )
          }
        />

        {/* Auth page */}
        <Route
          path="/app/auth"
          element={
            token ? (
              role && ['barista', 'manager', 'owner'].includes(role) ? (
                <Navigate to={resolveStaffHome(role)} replace />
              ) : (
                <Navigate to="/app/home" replace />
              )
            ) : (
              <AuthPage />
            )
          }
        />

        {/* Customer Routes */}
        <Route path="/app/home" element={<ProtectedRoute><AppLayout><HomeV3 /></AppLayout></ProtectedRoute>} />
        <Route path="/app/account" element={<ProtectedRoute><AppLayout><AccountPage /></AppLayout></ProtectedRoute>} />
        <Route path="/app/orders" element={<ProtectedRoute><AppLayout><OrdersPage /></AppLayout></ProtectedRoute>} />
        <Route path="/app/reviews" element={<ProtectedRoute><AppLayout><MyReviewsPage /></AppLayout></ProtectedRoute>} />
        <Route path="/app/wallet" element={<ProtectedRoute><AppLayout><WalletPage /></AppLayout></ProtectedRoute>} />
        <Route path="/app/menu" element={<ProtectedRoute><AppLayout><MenuPage /></AppLayout></ProtectedRoute>} />
        <Route path="/app/location" element={<ProtectedRoute><AppLayout><LocationPage /></AppLayout></ProtectedRoute>} />
        <Route path="/app/actus" element={<ProtectedRoute><AppLayout><ActusPage /></AppLayout></ProtectedRoute>} />
        <Route path="/app/shop/:id" element={<ProtectedRoute><AppLayout><ShopDetailPage /></AppLayout></ProtectedRoute>} />
        <Route path="/app/recharge" element={<ProtectedRoute><AppLayout><OnlineRechargePage /></AppLayout></ProtectedRoute>} />
        <Route path="/app/cart" element={<ProtectedRoute><AppLayout><CartPage /></AppLayout></ProtectedRoute>} />
        <Route path="/app/pay" element={<Navigate to="/app/cart" replace />} />

        {/* Staff Routes */}
        <Route path="/app/staff" element={<Navigate to="/login" replace />} />
        <Route path="/app/pos" element={<StaffRoute><StaffLayout><POSPage /></StaffLayout></StaffRoute>} />
        {/* MB-POS-V13 — Écran de préparation V1 (KDS). Toujours accessible par URL ; mis en avant dans le POS uniquement si service_mode=register_and_prep. */}
        <Route path="/app/preparation" element={<StaffRoute><StaffLayout><PreparationPage /></StaffLayout></StaffRoute>} />

        {/* SUPERKAWA OS — Home pilotage coffee shop (manager + owner) */}
        <Route path="/app/os" element={<OSRoute><StaffLayout><OSPage /></StaffLayout></OSRoute>} />

        {/* MB-ONBOARD-A3 — Wizard d'onboarding premier coffee shop (owner/manager) */}
        <Route path="/app/os/onboarding" element={<OSRoute><StaffLayout><OnboardingWizard /></StaffLayout></OSRoute>} />

        {/* Offres SaaS — déplacées du marketing public vers l'espace OS (MB-SAAS-SUBSCRIPTION-RELOCATION) */}
        <Route path="/app/os/abonnement" element={<OSRoute><StaffLayout><OSSubscriptionPage /></StaffLayout></OSRoute>} />

        {/* PAIEMENTS PHASE 2 — retour / refresh de l'onboarding Stripe Connect hébergé (pages minimales, sans 404) */}
        <Route path="/app/os/paiements" element={<OSRoute><StaffLayout><OSPaymentsPage /></StaffLayout></OSRoute>} />
        <Route path="/app/os/paiements/retour" element={<OSRoute><StaffLayout><OSPaymentsReturnPage /></StaffLayout></OSRoute>} />
        <Route path="/app/os/paiements/refresh" element={<OSRoute><StaffLayout><OSPaymentsRefreshPage /></StaffLayout></OSRoute>} />

        {/* MB-OS-DASH-5C — Écran roadmap « Bientôt » (Communication clients, Wallet owner) */}
        <Route path="/app/os/roadmap" element={<OSRoute><StaffLayout><OSRoadmapPage /></StaffLayout></OSRoute>} />

        {/* MB-WS-6 — Écran Équipe Workspace (gestion des membres) */}
        <Route path="/app/os/team" element={<OSRoute><StaffLayout><WorkspaceTeamPage /></StaffLayout></OSRoute>} />

        {/* MB-WS-7 — Cockpit Workspace consolidé (vue multi-établissements) */}
        <Route path="/app/os/workspace" element={<OSRoute><StaffLayout><WorkspaceDashboardPage /></StaffLayout></OSRoute>} />

        {/* MB-WS-9 — Paramètres Workspace (administration Organization, owner-only) */}
        <Route path="/app/os/workspace/settings" element={<OSRoute><StaffLayout><WorkspaceSettingsPage /></StaffLayout></OSRoute>} />
        <Route path="/app/os/mon-compte" element={<OSRoute><StaffLayout><WorkspaceMyAccountPage /></StaffLayout></OSRoute>} />
        <Route path="/app/os/workspace/establishments" element={<OSRoute><StaffLayout><EstablishmentsPage /></StaffLayout></OSRoute>} />

        {/* VIEW-ESTABLISHMENT-2 — Vue mono-store (drill-down depuis le Cockpit Owner) */}
        <Route path="/app/os/etablissement/:shopId" element={<OSRoute><StaffLayout><EstablishmentDetailPage /></StaffLayout></OSRoute>} />

        {/* ESPACE PLATEFORME SUPERKAWA (MB-NAV-1) — réservé admins plateforme */}
        <Route path="/app/platform" element={<PlatformRoute><StaffLayout><PlatformPage /></StaffLayout></PlatformRoute>} />
        <Route path="/app/platform/crm/dossiers/:id" element={<PlatformRoute><StaffLayout><ClientDossierPage /></StaffLayout></PlatformRoute>} />

        {/* Admin Routes */}
        <Route path="/app/admin" element={<AdminRoute><StaffLayout><AdminPage /></StaffLayout></AdminRoute>} />
        <Route path="/app/admin/audit-log" element={<AdminRoute><StaffLayout><AuditLogPage /></StaffLayout></AdminRoute>} />
        {/* P0-PROD-COST-SEED — Diagnostic lecture seule, admin plateforme (SEC-LOT-1 : apply supprimé). */}
        <Route path="/app/admin/p0-maintenance" element={<PlatformRoute><StaffLayout><P0Maintenance /></StaffLayout></PlatformRoute>} />

        {/* ═══════ BACKWARD COMPAT REDIRECTS ═══════ */}
        <Route path="/auth" element={<Navigate to="/app/auth" replace />} />
        <Route path="/wallet" element={<Navigate to="/app/wallet" replace />} />
        <Route path="/home" element={<Navigate to="/app/home" replace />} />
        <Route path="/menu" element={<Navigate to="/app/menu" replace />} />
        <Route path="/cart" element={<Navigate to="/app/cart" replace />} />
        <Route path="/pay" element={<Navigate to="/app/pay" replace />} />
        <Route path="/pos" element={<Navigate to="/app/pos" replace />} />
        <Route path="/staff" element={<Navigate to="/login" replace />} />
        <Route path="/admin" element={<Navigate to="/app/admin" replace />} />
        <Route path="/admin/audit-log" element={<Navigate to="/app/admin/audit-log" replace />} />
        <Route path="/account" element={<Navigate to="/app/home" replace />} />
        <Route path="/location" element={<Navigate to="/app/location" replace />} />
        <Route path="/recharge" element={<Navigate to="/app/recharge" replace />} />
        <Route path="/landing" element={<Navigate to="/" replace />} />
        <Route path="/configurer" element={<Navigate to="/solutions" replace />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    bootstrapAnalytics(); // GA4 ré-init si consentement déjà 'accepted' (opt-in strict)
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <LanguageProvider>
            <ShopProvider>
              <SupportAssistantProvider>
                <AppRoutes />
              </SupportAssistantProvider>
            </ShopProvider>
          </LanguageProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
